// Finance settings — night-audit behaviour, VAT, tourist tax, report recipient.
import React, { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { apiCall } from './_shared';

const Field = ({ label, hint, children }) => (
  <label className="block text-xs text-slate-600">
    {label}
    {children}
    {hint && <span className="block text-[11px] text-slate-400 mt-0.5">{hint}</span>}
  </label>
);

const inputCls = 'mt-1 w-full border rounded-lg px-3 py-2 text-sm';

const FinanceSettingsTab = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { apiCall('/api/admin/finance/settings').then(setS).catch((e) => setMsg(e.message)); }, []);

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const { updated_at, updated_by, ...body } = s;
      setS(await apiCall('/api/admin/finance/settings', { method: 'PUT', body: JSON.stringify(body) }));
      setMsg('Saved.');
    } catch (e) { setMsg(e.message); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>;
  const num = (k, step = '0.01') => (
    <input type="number" step={step} value={s[k]} onChange={(e) => setS({ ...s, [k]: parseFloat(e.target.value) || 0 })} className={inputCls} data-testid={`finance-setting-${k}`} />
  );
  const bool = (k, label) => (
    <label className="flex items-center gap-2 text-sm text-slate-700">
      <input type="checkbox" checked={!!s[k]} onChange={(e) => setS({ ...s, [k]: e.target.checked })} data-testid={`finance-setting-${k}`} /> {label}
    </label>
  );

  return (
    <div className="bg-white rounded-xl border p-5 space-y-5 max-w-3xl" data-testid="finance-settings">
      <div>
        <h3 className="font-semibold text-slate-900 text-sm">Night audit</h3>
        <div className="grid sm:grid-cols-2 gap-4 mt-2">
          <Field label="Auto-run hour (Podgorica time)" hint="Closes the previous business day once this hour is reached.">
            {num('night_audit_hour', '1')}
          </Field>
          <Field label="Accommodation VAT rate" hint="Montenegro: 7% for accommodation.">
            {num('room_vat_rate')}
          </Field>
          <div className="space-y-2 sm:col-span-2">
            {bool('auto_post_room_charges', 'Post nightly room charges to checked-in folios')}
            {bool('release_no_shows_in_beds24', 'Cancel no-shows in Beds24 automatically (puts the room back on sale)')}
          </div>
        </div>
      </div>
      <div>
        <h3 className="font-semibold text-slate-900 text-sm">Tourist tax (boravišna taksa)</h3>
        <div className="grid sm:grid-cols-3 gap-4 mt-2">
          <Field label="Adult rate € / night">{num('tourist_tax_adult')}</Field>
          <Field label="Child factor (12–18)" hint="0.5 = half price">{num('tourist_tax_child_factor')}</Field>
          <Field label="Free under age">{num('tourist_tax_child_free_under', '1')}</Field>
        </div>
      </div>
      <div>
        <h3 className="font-semibold text-slate-900 text-sm">Flash report</h3>
        <Field label="Email recipient" hint="Leave empty to use the owner digest address.">
          <input value={s.report_email || ''} onChange={(e) => setS({ ...s, report_email: e.target.value })} placeholder="owner@larosanegra.com" className={inputCls} data-testid="finance-setting-report_email" />
        </Field>
      </div>
      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold flex items-center gap-1 disabled:opacity-60" data-testid="finance-settings-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save settings
        </button>
        {msg && <span className="text-xs text-slate-600" data-testid="finance-settings-msg">{msg}</span>}
        {s.updated_by && <span className="text-xs text-slate-400 ml-auto">Last saved by {s.updated_by}</span>}
      </div>
    </div>
  );
};

export default FinanceSettingsTab;
